import React, { ReactNode, useEffect, useState } from 'react';
import { Box, Tabs, Tab, Typography, ToggleButton, Stack } from '@mui/material';
import { GiRun, GiMountainClimbing } from 'react-icons/gi';
import { Profile } from '@par/shared/types/player';
import { GameType, GameVariant } from '../play-menu';
import { SurvivalMode } from '.';
import { CategorySelect } from './CategorySelect';
import { SurvivalToggleButtonGroup } from './mui-styles';
import { TabLabel } from './TabLabel';
import { ToggleCasualPro } from './ToggleCasualPro';
import { TabPanel } from './TabPanel';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Typography>{children}</Typography>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `solo-tab-${index}`,
    'aria-controls': `solo-tabpanel-${index}`,
  };
};

type PropTypes = {
  gameType: GameType;
  gameVariant: GameVariant;
  profile?: Profile;
  survivalMode: SurvivalMode;
  qualifiesForPunchcardPlay: (opCategory: string) => void;
  handleCasualProChange: (event: any, value: 'casual' | 'pro') => void;
  handleGameVariantChange: (event: any, value: 'normal' | 'survival') => void;
  handleSurvivalModeChange: (
    event: React.MouseEvent<HTMLElement>,
    value: 'challenge' | 'endurance'
  ) => void;
  updatedCheckedCategories: (categories: any) => void;
  updatedCheckedTopCategories: (topCategories: any) => void;
};

export const PlayMenuTabsSolo = ({
  gameType,
  gameVariant,
  profile,
  survivalMode,
  qualifiesForPunchcardPlay,
  handleCasualProChange,
  handleGameVariantChange,
  handleSurvivalModeChange,
  updatedCheckedCategories,
  updatedCheckedTopCategories,
}: PropTypes) => {
  const tabPanelValue = {
    normal: 0,
    survival: 1,
  };

  return (
    <>
      <Box
        sx={{
          marginTop: '0px !important',
          backgroundColor: '#f9f9f90d',
        }}
      >
        <Tabs
          value={gameVariant}
          onChange={handleGameVariantChange}
          indicatorColor="secondary"
          centered
          sx={{ width: '100%', flex: 1 }}
        >
          <Tab
            label={
              <TabLabel text="Normal" isActiveTab={gameVariant === 'normal'} />
            }
            {...a11yProps(0)}
            sx={{ flex: 1 }}
            value="normal"
          />
          <Tab
            label={
              <TabLabel
                text="Survival"
                isActiveTab={gameVariant === 'survival'}
              />
            }
            {...a11yProps(1)}
            sx={{ flex: 1 }}
            value="survival"
          />
        </Tabs>
      </Box>
      <TabPanel value={tabPanelValue[gameVariant]} index={0}>
        <ToggleCasualPro
          gameType={gameType}
          handleCasualProChange={handleCasualProChange}
        />
        <CategorySelect
          initCategorySelections={
            profile?.recentPlayMenuSettings?.categorySelections || []
          }
          qualifiesForPunchcardPlay={qualifiesForPunchcardPlay}
          updatedCheckedCategories={updatedCheckedCategories}
          updatedCheckedTopCategories={updatedCheckedTopCategories}
        />
      </TabPanel>
      <TabPanel value={tabPanelValue[gameVariant]} index={1}>
        <Box display={'flex'} justifyContent={'center'} marginX={8} marginY={1}>
          <SurvivalToggleButtonGroup
            color="primary"
            value={survivalMode}
            exclusive
            onChange={handleSurvivalModeChange}
          >
            <ToggleButton
              value="challenge"
              sx={{
                flex: 1,
              }}
            >
              <Stack spacing={4} height={'100%'}>
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  justifyContent={'center'}
                >
                  <GiMountainClimbing
                    size={24}
                    color="#8fff0a"
                    style={{ marginRight: 8 }}
                  />
                  <Typography
                    variant={'h6'}
                    textAlign={'center'}
                    color="#8fff0a"
                  >
                    Challenge Tower
                  </Typography>
                </Stack>
                <Typography variant={'subtitle2'} textAlign={'center'}>
                  Work your way up the tower as new challenges are presented
                  every 10 questions
                </Typography>
              </Stack>
            </ToggleButton>
            <ToggleButton
              value="endurance"
              sx={{
                flex: 1,
              }}
            >
              <Stack spacing={4} height={'100%'}>
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  justifyContent={'center'}
                >
                  <GiRun size={24} color="#8fff0a" style={{ marginRight: 8 }} />
                  <Typography
                    variant={'h6'}
                    textAlign={'center'}
                    color="#8fff0a"
                  >
                    Endurance
                  </Typography>
                </Stack>
                <Typography variant={'subtitle2'} textAlign={'center'}>
                  Don't time out for as many questions as you can
                </Typography>
              </Stack>
            </ToggleButton>
          </SurvivalToggleButtonGroup>
        </Box>
      </TabPanel>
    </>
  );
};
